import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Plus, X as XIcon, Sparkles } from "lucide-react";
import { getCsrfToken } from "../lib/auth";
import { guardedFocus } from "../lib/focus-guard";

/** Wave 4 item 4: universal Quick Capture.
 *
 *  A single global "+" affordance reachable from every cockpit
 *  surface (fixed FAB) and via Cmd+Shift+K. The note drops into a
 *  portal modal so it floats above any transformed card. The server
 *  classifies the thought (heuristic first, local Ollama for the
 *  ambiguous tail) into note | wish | commitment | task | reminder |
 *  decision | reflection and routes it. The user can override the
 *  inferred kind before it is even shown by picking a chip. */
const KINDS = [
  "auto",
  "note",
  "wish",
  "commitment",
  "task",
  "reminder",
  "decision",
  "reflection",
] as const;

export function QuickCaptureFab() {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const [kind, setKind] = useState<string>("auto");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const taRef = useRef<HTMLTextAreaElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.shiftKey && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen(true);
      } else if (e.key === "Escape" && open) {
        setOpen(false);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  useEffect(() => {
    if (open) {
      // W015 focus guard: the deferred focus lets the portal paint,
      // but during that gap the user may switch tabs or keep typing in
      // another field. guardedFocus pauses on tab-blur and refuses to
      // steal the caret from an editable element outside this surface.
      setTimeout(() => guardedFocus(taRef.current, panelRef.current), 50);
      setResult(null);
      setError(null);
    } else {
      setText("");
      setKind("auto");
      setBusy(false);
    }
  }, [open]);

  const submit = async () => {
    const body = text.trim();
    if (!body || busy) return;
    setBusy(true);
    setError(null);
    try {
      const tok = getCsrfToken();
      const res = await fetch("/api/quick-capture", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(tok ? { "X-CSRF-Token": tok } : {}),
        },
        credentials: "same-origin",
        body: JSON.stringify({
          text: body,
          category: kind === "auto" ? "" : kind,
        }),
      });
      const j = await res.json();
      if (!res.ok || !j.ok) throw new Error(j.error || "capture failed");
      setResult(j.kind);
      setText("");
      setTimeout(() => setOpen(false), 900);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label="Quick capture"
        title="Quick capture (Cmd+Shift+K)"
        className="fixed z-[65] right-4 bottom-20 md:bottom-6 w-14 h-14 rounded-full bg-sky-600 hover:bg-sky-500 text-white shadow-xl shadow-sky-600/30 inline-flex items-center justify-center transition-colors active:scale-95"
      >
        <Plus className="w-7 h-7" />
      </button>

      {open &&
        createPortal(
          <div
            className="fixed inset-0 z-[80] bg-black/40 backdrop-blur-sm flex items-start sm:items-center justify-center p-4 sm:p-12"
            onClick={() => setOpen(false)}
            role="dialog"
            aria-modal="true"
            aria-label="Quick capture"
          >
            <div
              ref={panelRef}
              onClick={(e) => e.stopPropagation()}
              className="w-full max-w-lg bg-white dark:bg-slate-800 rounded-2xl shadow-2xl border border-slate-200 dark:border-slate-700 overflow-hidden"
            >
              <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200 dark:border-slate-700">
                <div className="flex items-center gap-2 text-sm font-semibold text-slate-900 dark:text-slate-100">
                  <Sparkles className="w-4 h-4 text-sky-500" />
                  Quick capture
                </div>
                <button
                  type="button"
                  onClick={() => setOpen(false)}
                  aria-label="Close"
                  className="w-7 h-7 rounded-full inline-flex items-center justify-center text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-700"
                >
                  <XIcon className="w-3.5 h-3.5" />
                </button>
              </div>
              <div className="p-4 space-y-3">
                <textarea
                  ref={taRef}
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  onKeyDown={(e) => {
                    if ((e.metaKey || e.ctrlKey) && e.key === "Enter") submit();
                  }}
                  rows={4}
                  placeholder="Capture a thought, a wish, a commitment… Memso will sort it."
                  className="w-full resize-none rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900/40 px-3 py-2.5 text-sm text-slate-900 dark:text-slate-100 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-sky-500/40"
                />
                <div className="flex flex-wrap gap-1.5">
                  {KINDS.map((k) => (
                    <button
                      key={k}
                      type="button"
                      onClick={() => setKind(k)}
                      className={`text-[11px] px-2 py-1 rounded-full font-medium transition-colors ${
                        kind === k
                          ? "bg-sky-600 text-white"
                          : "bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-600"
                      }`}
                    >
                      {k === "auto" ? "auto-classify" : k}
                    </button>
                  ))}
                </div>
                {result && (
                  <div className="text-sm text-emerald-600 dark:text-emerald-400 font-medium">
                    Captured as {result}.
                  </div>
                )}
                {error && (
                  <div className="text-sm text-rose-600 dark:text-rose-400">
                    {error}
                  </div>
                )}
                <div className="flex items-center justify-between pt-1">
                  <span className="text-[11px] text-slate-400">
                    Cmd+Enter to save
                  </span>
                  <button
                    type="button"
                    onClick={submit}
                    disabled={busy || !text.trim()}
                    className="rounded-lg bg-sky-600 hover:bg-sky-500 disabled:opacity-50 text-white text-sm font-semibold px-4 py-2 transition-colors"
                  >
                    {busy ? "Saving…" : "Capture"}
                  </button>
                </div>
              </div>
            </div>
          </div>,
          document.body,
        )}
    </>
  );
}
